import {MsgLabel, OMsgLabelInit, OSkinableInit} from "/@back@/commons/basis.js";
import {IReg, IRegPointer, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {GridColDef, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";
import {ICoreUniverseEnv} from "/@lib@/core/universe.js";
import {AccelKeyMgr, Action, ACTION, ActionMenu, ActionWrapper, IAction, IActionToggle} from "/@lib@/commons/actions.js";
import {CellBuilderDate, CellBuilderEnum, CellBuilderIconLabel, CellBuilderNumber, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {BaseAreaViewAsync, IView} from "/@lib@/commons/views.js";
import "/@back@/core/dialogs/dialogs_Perms.js";
import {EJobStatus, EXECUTOR, IExecutorInUniverse, IExecutorInUniversePointer, IJobBuilderActionContext, IJobsCtx, JJob, JJobBase, JJobCriterion, JJobsStats, UiJobFactory} from "/@lib@/core/executor.js";
import {IJobGridCtx, JobsGrid, OJobsGridInit} from "/@back@/core/widgets/jobsGrid.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {BarShared, OBarSharedInit} from "/@back@/commons/widgets/bars.js";
import {CellBuilderAccount} from "/@back@/core/widgets/usersGrid.js";
import {OTabsInit, Tab, Tabs} from "/@back@/commons/widgets/tabs.js";
import {AreaAsync, IArea} from "/@lib@/commons/areas.js";
import {GridDataRowJsonTree} from "/@back@/commons/widgets/tree.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {ActionDeleteJobs, ActionForceExecuteJobs, ActionMoveInQueueJobs, JobsAction} from "/@back@/core/actions/jobsActions.js";
import {Resizer} from "/@back@/commons/widgets/resizer.js";

export interface OJobsMgrInit extends OSkinableInit {
	reg: IReg<ICoreUniverseEnv & IExecutorInUniverse & IUiEnv>,
	scope?: 'my' | 'all',
	/** Liste de promises de UiJobFactory, ou de fctions retournant cette promise. Liste issue de executor:jobs:factories à défaut */
	jobsFactoriesList?: [],
	/** Init de=u tab */
	jobsTab?: OTabsInit<any>,
	/** Init des grids */
	jobsGrids?: OJobsGridInit<any>,
	/** Tab à afficher */
	showTab?: EJobTabStatus
}

enum EJobTabStatus {
	alive = 'alive',
	planned = 'planned',
	failed = 'failed',
	done = 'done',
}


export class JobsMgr<E extends IRegPointer<IExecutorInUniversePointer & IUiEnv>, D extends IJobBuilderActionContext> extends BaseAreaViewAsync<E> implements IGridLineDrawer {
	reg: IReg<IExecutorInUniversePointer & IUiEnv>;

	params: OJobsMgrInit;

	uiJobsFactories: UiJobFactory<E, D>[];

	protected _headband: MsgLabel;

	protected _tabs: JobsTabs;

	protected _toolbar: BarShared<JobsMgr<any, any>>;

	protected _fetching: "error" | "pending" | null;

	/** Mise en pause de l'éxécution des jobs */
	get frozenStatus(): boolean {return this.hasAttribute("data-frozen")};

	set frozenStatus(val: boolean) {
		DOM.setAttrBool(this, "data-frozen", val);
	}

	protected async _initialize(init: OJobsMgrInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.params = Object.assign({
			jobsFactoriesList: [EXECUTOR.JOBS_FACTORIES_LIST],
		}, init);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		const uiJobsFactoriesFcts: (() => Promise<UiJobFactory<any, any>>)[] = this.reg.mergeLists(...this.params.jobsFactoriesList) as any;
		const uiJobsFactoriesPromises = uiJobsFactoriesFcts ? uiJobsFactoriesFcts.map((entry) => typeof entry === "function" ? entry.call(this) : entry) : null;
		this.uiJobsFactories = await Promise.all(uiJobsFactoriesPromises || []) as UiJobFactory<any, any>[];

		// Actions
		this.reg.addToList("actions:jobsmgr:toolbar", "actionFreezeExecutor", 1, new FreezeExecutorBtn());
		this.reg.addToList("actions:jobsmgr:toolbar", "actionRefreshUi", 1, actionRefreshUi);
		this.reg.addToList("actions:jobsmgr:toolbar", "actionNewJob", 1, ActionNewJobs.SINGLETON);

		this.reg.addToList("actions:jobsmgr", "actionDuplicateJob", 1, new ActionWrapperRefresMgr(this).setOverridenSvc(new ActionWrapperCreateJob(this).setOverridenSvc(new ActionDuplicateJob(this))));
		this.reg.addToList("actions:jobsmgr", "actionDeleteJobs", 1, new ActionWrapperRefresMgr(this).setOverridenSvc(ActionDeleteJobs.SINGLETON));
		//this.reg.addToList("actions:jobsmgr", "actionScheduleJobs", 1, new ActionWrapperRefresMgr(this).setOverridenSvc(ActionScheduleJobs.SINGLETON));
		this.reg.addToList("actions:jobsmgr", "actionForceExecuteJobs", 1, new ActionWrapperRefresMgr(this).setOverridenSvc(ActionForceExecuteJobs.SINGLETON));
		this.reg.addToList("actions:jobsmgr", "actionMoveInQueueJobs", 1, new ActionWrapperRefresMgr(this).setOverridenSvc(ActionMoveInQueueJobs.SINGLETON));

		this._headband = sr.appendChild(<MsgLabel class="headband" î={{
			label: "Exécution des traitements gelée",
			icon: "/@skin@/core/actions/jobs/frozen.svg",
			level: "warning",
		} as OMsgLabelInit}/>) as MsgLabel;

		let jbBoxElt = sr.appendChild(<div class="jobsBox"/>) as HTMLDivElement;

		// Colonnes
		const col_id = new GridColDef('id')
			.setLabel("Id.")
			.setDescription("Identifiant")
			.setSortable(true)
			.setCellBuilder(new CellBuilderNumber("id"));
		const col_name = new GridColDef('name')
			.setLabel("Label")
			.setFlex("6rem", 1, 1)
			.setCellBuilder(new CellBuilderJobLabel(this));
		const col_lastQueued = new GridColDef('lastQueued')
			.setLabel("Exécution")
			.setDescription("Date d'exécution")
			.setFlex("8rem", 1, 1)
			.setCellBuilder(new CellBuilderDate("lastQueued").setCellClass("center").setOptions({year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"}));
		const col_planned = new GridColDef('planned')
			.setLabel("Planification")
			.setDescription("Date planifiée")
			.setFlex("8rem", 1, 1)
			.setCellBuilder(new CellBuilderDate("planned").setCellClass("center").setOptions({year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"}));
		const col_createdBy = new GridColDef('createdBy')
			.setLabel("Créateur")
			.setDescription("Compte ayant créé ce traitement")
			.setFlex("3rem", 1, 1)
			.setCellBuilder(new CellBuilderAccount(this.reg, "createdBy", true));
		const col_status = new GridColDef('lastStatus')
			.setLabel("Statut")
			.setFlex('2rem', 1, 1)
			.setCellBuilder(new CellBuilderEnum('lastStatus', new Map([
				[EJobStatus.waiting, "En attente"],
				[EJobStatus.launched, "Chargé"],
				[EJobStatus.pending, "En cours d'exécution"],
				[EJobStatus.done, "Terminé"],
				[EJobStatus.failed, "En échec"],
				[EJobStatus.planned, "Planifié"],
			])).setCellClass("center"));

		/** Tabs */
		let areas: IArea[] = [];
		areas.push(new AreaAsync(EJobTabStatus.alive)
			.setLabel("En cours").setDescription("Traitements en cours")
			.setBodyBuilder((ctx) => {
				return <JobsGridView id={EJobTabStatus.alive} î={Object.assign({
					columnDefs: [col_id, col_name, col_lastQueued, col_createdBy, col_status],
					reg: this.reg,
					emptyBody: this.emptyBody,
					skinOver: 'jobs-mgr/grid',
					actions: this.reg.mergeLists("actions:jobsmgr", "actions:jobsmgr:alive"),
					accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:jobsmgr", "accelkeys:jobsmgr:alive")),
					selectAction: new DisplayJobDetails(this),
					lineDrawer: this,
				} as OJobsGridInit<any>, init.jobsGrids)}/>
			}));
		if (this.reg.env.universe.executor.hasFeature("scheduling")) areas.push(new AreaAsync(EJobTabStatus.planned)
			.setLabel("Planifiés").setDescription("Traitements planifiés")
			.setBodyBuilder((ctx) => {
				return <JobsGridView id={EJobTabStatus.planned} î={Object.assign({
					columnDefs: [col_id, col_name, col_planned, col_createdBy],
					reg: this.reg,
					emptyBody: this.emptyBody,
					skinOver: 'jobs-mgr/grid',
					actions: this.reg.mergeLists("actions:jobsmgr", "actions:jobsmgr:planned"),
					accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:jobsmgr", "accelkeys:jobsmgr:planned")),
					selectAction: new DisplayJobDetails(this),
					lineDrawer: this,
				} as OJobsGridInit<any>, init.jobsGrids)}/>
			}));
		areas.push(new AreaAsync(EJobTabStatus.failed)
			.setLabel("En échec").setDescription("Traitements en échec")
			.setBodyBuilder((ctx) => {
				return <JobsGridView id={EJobTabStatus.failed} î={Object.assign({
					columnDefs: [col_id, col_name, col_lastQueued, col_createdBy],
					reg: this.reg,
					emptyBody: this.emptyBody,
					skinOver: 'jobs-mgr/grid',
					actions: this.reg.mergeLists("actions:jobsmgr", "actions:jobsmgr:failed"),
					accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:jobsmgr", "accelkeys:jobsmgr:failed")),
					selectAction: new DisplayJobDetails(this),
					lineDrawer: this,
				} as OJobsGridInit<any>, init.jobsGrids)}/>
			}));
		areas.push(new AreaAsync(EJobTabStatus.done)
			.setLabel("Terminés").setDescription("Traitements terminés")
			.setBodyBuilder((ctx) => {
				return <JobsGridView id={EJobTabStatus.done} î={Object.assign({
					columnDefs: [col_id, col_name, col_lastQueued, col_createdBy],
					reg: this.reg,
					emptyBody: this.emptyBody,
					skinOver: 'jobs-mgr/grid',
					actions: this.reg.mergeLists("actions:jobsmgr", "actions:jobsmgr:done"),
					accelKeyMgr: new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:jobsmgr", "accelkeys:jobsmgr:done")),
					selectAction: new DisplayJobDetails(this),
					lineDrawer: this,
				} as OJobsGridInit<any>, init.jobsGrids)}/>
			}));
		this._tabs = jbBoxElt.appendChild(<JobsTabs î={Object.assign({
			vertical: true,
			areas: areas,
		} as OTabsInit<any>, init.jobsTab)} id="tabs"/>) as JobsTabs;

		/** Toolbar */
		this._toolbar = jbBoxElt.appendChild(new BarShared<this>().initialize({
			reg: this.reg,
			startActions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:jobsmgr:toolbar", "actions:jobsmgr:toolbar:" + this.reg.env.universe.getId()),
				this.reg.getPref("actions.jobsmgr.groupOrder", "refresh add *"), this) as Action<this>[],
			actionContext: this,
			focusListening: this._tabs,
		} as OBarSharedInit<this>));
		DOM.setAttrBool(this._toolbar, "vertical", true);

		this.addEventListener("keydown", (ev) => {
			if (ev.key === 'Escape' && this.currentJobsGrig) {
				this.currentJobsGrig.selectByJob([]);
				ev.stopPropagation()
			}
		});
	}

	async onViewShown() {
		this._tabs.onViewShown();
		if (this.params.showTab)
			await this._tabs.selectTab(this._tabs.getTabByCode(this.params.showTab));
		return this.fetchAll();
	}

	get currentJobsGrig(): JobsGridView {
		return this._tabs.selectedTab ? this._tabs.selectedTab.view as JobsGridView : null;
	}

	/** Rafraichissement global des données affichées */
	async fetchAll(excludeCurrent: boolean = true) {
		await this.fetchTabJobs();
		await this.fetchStats();
	}

	/** Rafraichit les compteurs des tabs et l'état d'exécution
	 * @param stats : stats computés en amont. Si spécifé, refreh ui uniquement
	 */
	async fetchStats(stats?: JJobsStats) {
		if (this._fetchingCountersPending) return;
		let currentTab = this._tabs.selectedTab;
		if (!currentTab) return;
		let currentTabStatus = currentTab.area.getId() as EJobTabStatus;
		try {
			this._fetchingCountersPending = true;
			if (!stats)
				stats = await EXECUTOR.getStats(this.reg.env.universe.executor);
			this._tabs.refreshCounterValue(EJobTabStatus.alive, stats.countInQueue.toString());
			this._tabs.refreshCounterValue(EJobTabStatus.failed, stats.countFailed.toString());
			this.frozenStatus = stats.frozen;
		} catch (e) {
			this._tabs.refreshCounterValue(EJobTabStatus.alive, '-');
			this._tabs.refreshCounterValue(EJobTabStatus.failed, '-');
			this.frozenStatus = null;
			await ERROR.log("Count jobs failed", e);
		} finally {
			this._fetchingCountersPending = false;
		}
		this._toolbar.refresh();
	}

	private _fetchingCountersPending = false;

	/** Rafraichit la tab courante affichée */
	protected async fetchTabJobs() {
		if (this._fetchingTabJobsPending) return;
		let currentTab = this._tabs.selectedTab;
		if (!currentTab) return;
		let currentTabStatus = currentTab.area.getId() as EJobTabStatus;
		this._fetching = "pending";
		try {
			this._fetchingTabJobsPending = true;
			let jobCriterions: JJobCriterion = this.computeFetchCriterion(currentTabStatus);
			const result = this.params.scope == "my" ?
				await EXECUTOR.findMyJobs(this.reg.env.universe.executor, jobCriterions) :
				await EXECUTOR.findJobs(this.reg.env.universe.executor, jobCriterions);

			this._fetching = null;
			const currentSelect = this.currentJobsGrig.getSelectedJobs();
			this.currentJobsGrig.jobsGridDatas.setDatas(result.jobs);
			this.currentJobsGrig.selectByJob(currentSelect);
		} catch (e) {
			this._fetching = "error";
			this.currentJobsGrig.jobsGridDatas.setDatas([]);
			await ERROR.log("List jobs failed", e);
		} finally {
			this._fetchingTabJobsPending = false;
		}
	}

	private _fetchingTabJobsPending = false;

	/** */
	private computeFetchCriterion(jobStatus: EJobTabStatus): JJobCriterion {
		// TODO : critère de filtre apporté par le init
		let jobCriterions: JJobCriterion = {};
		if (jobStatus == EJobTabStatus.alive) {
			jobCriterions.statuses = [EJobStatus.waiting, EJobStatus.launched, EJobStatus.pending];
			jobCriterions.inQueue = true;
		} else if (jobStatus == EJobTabStatus.done)
			jobCriterions.statuses = [EJobStatus.done]
		else if (jobStatus == EJobTabStatus.failed)
			jobCriterions.statuses = [EJobStatus.failed]
		else if (jobStatus == EJobTabStatus.planned)
			jobCriterions.statuses = [EJobStatus.planned]
		return jobCriterions;
	}

	/**
	 * Tree
	 */
	redrawLine(row: GridDataRowJson<JJobBase>, line: HTMLElement): void {
		DOM.setAttr(line, "data-status", row.rowDatas.lastStatus);
	}

	emptyBody(): Element {
		switch (this._fetching) {
		case "pending":
			return <c-msg label="Chargement en cours..." level="loading"/>
		case "error":
			return <c-msg label="Chargement en erreur" level="error"/>
		default:
			return <c-msg label="Aucun traitement"/>
		}
	}

	/**
	 * IViewApi
	 */
	onViewBeforeHide(close?: boolean): boolean {
		return false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return true;
		/*return !await this.computeDirtyStatus() || await POPUP.confirm(`Des propriétés n'ont pas été enregistrées. Voulez-vous abandonner ces modifications et quitter ?`, this.reg.env.uiRoot, {
			okLbl: "Abandonner les modifications et quitter",
			cancelLbl: "Reprendre l'édition"
		});*/
	}

}

customElements.define("jobs-mgr", JobsMgr);

REG.reg.registerSkin('jobs-mgr', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	c-bar-shared {
		flex: none;
	}

	#tabs {
		display: contents;
	}

	server-jobs-grid {
		flex: 1;
	}

	jobs-mgr-gridview {
		border: none;
	}

	:host(:not([data-frozen])) .headband {
		display: none;
	}

	.headband {
		background-color: var(--tooltip-bgcolor);
	}

	.jobsBox {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
	}

`);


REG.reg.registerSkin("jobs-mgr/grid", 1, /* language=CSS */ `
	.icon {
		padding-inline-start: 1.5em;
	}

	.late {
		color: var(--error-color);
	}

	*[data-status=launched],
	*[data-status=pending] {
		font-style: italic;
	}

`);

/** JobsGridView */
class JobsGridView extends JobsGrid implements IView {

	protected _detailsElt: HTMLDivElement;

	protected _resizerElt: Resizer;

	protected async _initialize(init: OJobsGridInit<IJobGridCtx>): Promise<void> {
		super._initialize(init);
		DOM.setAttrBool(this.shadowRoot.lastElementChild, "c-resizable", true);
		this._resizerElt = this.shadowRoot.appendChild(<c-resizer c-orient="column" class="detailsRsz"/>) as Resizer;
		this._detailsElt = this.shadowRoot.appendChild(<div class="details" c-resizable/>) as HTMLDivElement;
		await this.showJobDetail(null);
	}

	/**
	 * Details
	 */
	async showJobDetail(jobs: JJobBase[]): Promise<void> {
		const me = DOMSH.findHost<JobsMgr<any, any>>(this);
		if (jobs && jobs.length == 1) {
			let jobBase = jobs[0];
			this._currentJobDetailId = jobBase.id; //raceCond
			let job = await EXECUTOR.getJob(me.reg.env.universe.executor, jobBase.id);
			this._detailsElt.innerText = "";
			if (this._currentJobDetailId !== job.id) return; //raceCond
			let factory = me.uiJobsFactories.find((factory) => factory.matchJob(jobBase));
			if (factory) {
				await factory.buildJobDetails(this._detailsElt, me, job);
				if (this._currentJobDetailId !== job.id) return; //raceCond
				DOM.setHidden(this._detailsElt, false);
				DOM.setHidden(this._resizerElt, false);
				this.ensureRowVisibleByJobId(job.id);
				this._currentJobDetailId = undefined;
			} else {
				DOM.setHidden(this._detailsElt, false);
				DOM.setHidden(this._resizerElt, false);
				this._detailsElt.appendChild(UiJobFactory.buildJobTitleDetails(job));
				this._detailsElt.appendChild(UiJobFactory.buildJobPropsDetails(job));
				this._detailsElt.appendChild(UiJobFactory.buildJobTriesDetails(job, me.reg));
			}
		} else {
			DOM.setHidden(this._detailsElt, true);
			DOM.setHidden(this._resizerElt, true);
		}
	}

	private _currentJobDetailId: string;


	onViewShown(): void {
		const me = DOMSH.findHost<JobsMgr<any, any>>(this);
		if (me) me.fetchAll();
	}
}

customElements.define("jobs-mgr-gridview", JobsGridView);
REG.reg.registerSkin("jobs-mgr-gridview", 1, /* language=CSS */ `
	.detailsRsz {
		cursor: n-resize;
	}

	.details:not([hidden]) {
		background-color: var(--row-bgcolor);
		border-block-start: 1px solid var(--border-color);
		padding: 2px;
		font-size: .8em;
		overflow: auto;
		height: max(20%, 6rem);
	}

	.details h1 {
		font-size: 1.2em;
		margin-block-start: 0;
	}

	.details h2 {
		font-size: 1em;
		letter-spacing: 1px;
		font-weight: bolder;
	}

	.details .content {
		margin-inline-start: 2rem;
	}

	.details .code {
		font-size: .8rem;
		font-family: var(--font-mono);
		border: 1px solid var(--alt1-border-color);
		padding: .3rem;
		user-select: text;
	}

`);


/** JobsTabs */
class JobsTabs extends Tabs {
	private _countersElts: Map<EJobTabStatus, HTMLSpanElement> = new Map();

	initialize(init?: OTabsInit<any>): this {
		super.initialize(init);
		this.findReg(init).installSkin("c-tabs", this.shadowRoot);
		return this;
	}

	_initTab(tab: Tab<any>) {
		super._initTab(tab);
		tab._createLabel();
		REG.findReg(this).installSkin("jobs-mgr-jobstabs-tab", tab.shadowRoot);
		if ([EJobTabStatus.alive, EJobTabStatus.failed].includes(tab.area.getId() as EJobTabStatus))
			this._countersElts.set(tab.area.getId() as EJobTabStatus, tab.shadowRoot.appendChild(<span class={"count " + tab.area.getId()}>-</span>));
	}

	refreshCounterValue(jobTabStatus: EJobTabStatus, value: string) {
		if (this._countersElts.has(jobTabStatus))
			this._countersElts.get(jobTabStatus).textContent = value;
	}

}

customElements.define("jobs-mgr-jobstabs", JobsTabs);
REG.reg.registerSkin("jobs-mgr-jobstabs", 1, /* language=CSS */ `

`);
REG.reg.registerSkin("jobs-mgr-jobstabs-tab", 1, /* language=CSS */ `
	:host {
		min-width: 7em !important;
		text-align: start !important;
	}

	.label {
		/*flex:unset;*/
	}

	.count {
		background-color: var(--inv-bgcolor);
		color: var(--inv-color);
		border-radius: 3px;
		/*top: -.4rem;
		left: .4rem;
		position: relative;*/
		font-size: .7em;
		padding: 2px;
		line-height: .7rem;
		font-family: var(--font-mono);
		-webkit-text-stroke: initial;
	}

	.count.failed {
		background-color: var(--error-color);
	}

`);

/** Colonnes */
export class CellBuilderJobLabel extends CellBuilderIconLabel {

	constructor(public jobsMgr: JobsMgr<any, any>) {
		super(null);
	}

	private getJobUiFactory(row: GridDataRowJsonTree<JJob>): UiJobFactory<any, any> {
		if (!("_jobFactory" in row.cacheHolder) && this.jobsMgr.uiJobsFactories)
			row.cacheHolder['_jobFactory'] = this.jobsMgr.uiJobsFactories.find((factory) => factory.matchJob(row.rowDatas as JJob));
		return row.cacheHolder['_jobFactory'];
	}

	_getValue(row: GridDataRowJsonTree<JJob>) {
		if ("_jobLabel" in row.cacheHolder)
			return row.cacheHolder['_jobLabel'];
		return row.cacheHolder['_jobLabel'] = this.getJobUiFactory(row) ? this.getJobUiFactory(row).getLabel(this.jobsMgr) : row.rowDatas.jobSgn
	}

	_getIcon(row: GridDataRowJsonTree<JJob>) {
		if ("_jobIcon" in row.cacheHolder)
			return row.cacheHolder['_jobIcon'];
		return row.cacheHolder['_jobIcon'] = this.getJobUiFactory(row) ? this.getJobUiFactory(row).getIcon(this.jobsMgr) : null
	}
}

/** Actions */

/** Action refresh UI */
const actionRefreshUi = new Action<JobsMgr<any, any>>("refreshUi")
	.setLabel("Rafraichir")
	.setGroup("refresh")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute(async function (this: Action<JobsMgr<any, any>>, ctx: JobsMgr<any, any>, ev: Event) {
			ctx.fetchAll();
		}
	);

/** Action créate Job */
export class ActionNewJobs<C extends JobsMgr<any, any>> extends ActionMenu<C> {
	static SINGLETON = new ActionNewJobs();

	constructor(id?: string) {
		super(id);
		this._label = "Nouveau...";
		this._group = "new";
		this._description = "Créer un nouveau traitement";
		this._icon = "/@skin@/core/dialogs/jobs/addJob.svg";
		this._enablePerms = "action.jobsMgr#create.job";
	}

	isVisible(ctx: C): boolean {
		if (!ctx.uiJobsFactories) return false;
		return super.isVisible(ctx);
	}

	getActions(ctx: C): IAction<IRegPointer<IExecutorInUniversePointer & IUiEnv>>[] {
		const jobsFactories = ctx.uiJobsFactories;
		let actions: IAction<any>[] = [];
		jobsFactories.forEach((factory) => {if (factory.getJobBuilder()) actions.push(new ActionWrapperCreateJob(ctx).setOverridenSvc(factory.getJobBuilder()))});
		return actions;
	}
}

/** Action de duplication d'un job */
export class ActionDuplicateJob<C extends IJobsCtx> extends JobsAction<C> {

	constructor(protected jobsMgr: JobsMgr<any, any>, id?: string) {
		super(id);
		this._label = "Créer un nouveau traitement de même type";
		this._description = "Créer un nouveau traitement de même type";
		this.setMode("mono");
		this._group = "new";
		this._icon = "/@skin@/core/actions/jobs/clone.svg";
		this.requireVisiblePerm("action.jobsMgr#create.job");
	}

	isVisible(ctx: C): boolean {
		if (!this.jobsMgr.uiJobsFactories) return false;
		if (!ctx.jobs || ctx.jobs.length !== 1) return false;
		let factory = this.jobsMgr.uiJobsFactories.find((factory) => factory.matchJob(ctx.jobs[0]));
		if (!factory || !factory.getJobBuilder()) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: C, ev?: Event): Promise<JJob> {
		try {
			const factory = this.jobsMgr.uiJobsFactories.find((factory) => factory.matchJob(ctx.jobs[0]));
			const creator = factory.getJobBuilder();
			if (creator.isAvailable(ctx)) {
				let job = await EXECUTOR.getJob(ctx.reg.env.universe.executor, ctx.jobs[0].id);
				return await creator.execute(Object.assign({job}, ctx));
			}
		} catch (e) {
			await ERROR.report(`La création du traitement n'a pas pu aboutir. Veuillez réessayer ultérieurement.`, e);
			return null;
		}
	}
}


/** Wrapper des actions de création de job : notification, refresh, ... */
class ActionWrapperCreateJob<C extends IJobsCtx> extends ActionWrapper<C> {
	constructor(public jobsMgr: JobsMgr<any, any>) {
		super();
	}

	async execute(ctx: C, ev?: Event): Promise<any> {
		let result = await super.execute(ctx, ev);
		if (result) {
			let jobId = (result as JJob).id;
			let planned = (result as JJob).planned;
			let dateObj = planned ? new Date(planned) : null;
			let date = dateObj ? dateObj.toLocaleString() : null;
			POPUP.showNotifWarning(date ?
					`Le traitement a bien été planifié le ${date} avec l'identifiant '${jobId}'.` :
					`Le traitement a bien été créé avec l'identifiant '${jobId}'.`
				, this.jobsMgr);
			actionRefreshUi.executeIfAvailable(this.jobsMgr);
		} else
			POPUP.showNotifError(`Le traitement n'a pas été créé. Veuillez vérifier les paramètres saisis.`, this.jobsMgr);
		return result;
	}
}

/** Wrapper d'une action qui déclenche un refresh du tree */
class ActionWrapperRefresMgr<C extends IJobsCtx> extends ActionWrapper<C> {
	constructor(public jobsMgr: JobsMgr<any, any>) {
		super();
	}

	async execute(ctx: C, ev?: Event): Promise<any> {
		let result = await super.execute(ctx, ev);
		if (result) await this.jobsMgr.fetchAll();// Difficile d'etre plus fin (maj des compteurs, ...)
		if (typeof result === "object") {
			let job = result as JJobBase;
			this.jobsMgr.currentJobsGrig.selectByJob([job]);
		}
		return result;
	}
}

/** Action qui affiche le détail d'un job */
export class DisplayJobDetails extends Action<IJobsCtx> {

	constructor(public jobsMgr: JobsMgr<any, any>) {
		super('focusItem');
	}

	execute(ctx: IJobsCtx, ev?: Event) {
		this.jobsMgr.currentJobsGrig.showJobDetail(ctx.jobs);
	}
}

/** Rafraichissement de tous les fetchs des areas ouvertes */
class FreezeExecutorBtn<C extends JobsMgr<any, any>> extends Action<C> {
	constructor() {
		super('freezeExecutor');
		this._group = "refresh";
		this._icon = "/@skin@/core/actions/jobs/frozen.svg";
		this.requireEnabledPerm("action.jobsMgr#freeze.executor");
	}

	getLabel(ctx: C): string {
		let currentStatus = this.getDatas("toggle", ctx);
		return `Gèle/relance l'exécution des traitements en attente`;
	}

	isVisible(ctx: C): boolean {
		if (!ctx.reg.env.universe.executor.hasFeature("freezable"))
			return false;
		return super.isVisible(ctx);
	}

	isToggle(ctx: C): this is IActionToggle<C> {
		return true;
	}

	getDatas(api: "toggle", ctx: C): boolean {
		return ctx.frozenStatus;
	}

	async execute(ctx: C, ev?: Event): Promise<void> {
		let currentStatus = this.getDatas("toggle", ctx);
		let infos = await EXECUTOR.freezeExecutor(ctx.reg.env.universe.executor, !currentStatus);
		ctx.fetchStats(infos);
	}
}